
import React, { useState, useEffect } from 'react';
import { Professional } from '../types';
import { Link, useNavigate } from 'react-router-dom';
import { Edit3, ExternalLink, BarChart, Settings, PlusCircle, LayoutDashboard, Loader2, AlertCircle, Linkedin, Share2, ShieldCheck, Zap } from 'lucide-react';
import { listingService } from '../services/listingService';
import { authService, User } from '../services/authService';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(authService.getUser());

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const loadListings = async () => {
      if (user) {
        const data = await listingService.getUserListings(user.email);
        setListings(data);
      }
      setLoading(false);
    };

    loadListings();
  }, [navigate, user]);

  const handleShareLinkedIn = (pro: Professional) => {
    const profileUrl = `${window.location.origin}/#/professional/${pro.id}`;
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`;
    window.open(shareUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-brand-600" size={32} />
      </div>
    );
  }

  const verifiedListing = listings.find(l => l.isVerified);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Verification Alert (Mock) */}
        {!verifiedListing && listings.length > 0 && (
          <div className="mb-8 bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-center justify-between">
             <div className="flex items-center gap-3 text-amber-800">
                <AlertCircle size={20} />
                <p className="text-xs font-bold uppercase tracking-tight">Profile Verification Pending</p>
             </div>
             <p className="text-[10px] text-amber-600 font-bold uppercase tracking-widest">Waiting for Admin Approval</p>
          </div>
        )}

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
           <div>
              <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tight flex items-center gap-3">
                 <LayoutDashboard className="text-brand-600" /> Professional Dashboard
              </h1>
              <p className="text-gray-500 font-medium">Hello {user?.name}, manage your directory records here.</p>
           </div>
           <div className="flex gap-4">
             <Link to="/admin" className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-gray-300 transition">
                Master View
             </Link>
             <Link to="/join" className="bg-brand-900 text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-black transition">
                <PlusCircle size={18} /> New Listing
             </Link>
           </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
           {/* Summary Stats */}
           <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
                 <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">Account Overview</h3>
                 <div className="space-y-4">
                    <div className="flex justify-between items-center">
                       <span className="text-sm font-bold text-gray-600">Active Listings</span>
                       <span className="bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-xs font-black">{listings.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                       <span className="text-sm font-bold text-gray-600">Total Leads Received</span>
                       <span className="bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-xs font-black">0</span>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-50">
                       <div className="text-[10px] font-black text-gray-300 uppercase tracking-widest mb-2">Authenticated User</div>
                       <div className="text-xs font-bold text-gray-500 truncate">{user?.email}</div>
                    </div>
                 </div>
              </div>

              {/* Social Boost Section */}
              {verifiedListing && (
                <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl overflow-hidden relative border-4 border-white">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Linkedin size={80} />
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                     <ShieldCheck size={20} />
                     <h4 className="text-lg font-black uppercase tracking-tight leading-none">Boost Your Reach</h4>
                  </div>
                  <p className="text-blue-100 text-xs font-medium leading-relaxed mb-6">
                    Verified profiles receive 4x more engagement. Share your status on LinkedIn to generate social signals and improve your Google ranking.
                  </p>
                  <button 
                    onClick={() => handleShareLinkedIn(verifiedListing)}
                    className="w-full bg-white text-blue-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition shadow-lg hover:bg-blue-50 flex items-center justify-center gap-2"
                  >
                    <Share2 size={14} /> Share Verified Profile
                  </button>
                </div>
              )}

              <div className="bg-brand-900 text-white p-8 rounded-3xl shadow-xl overflow-hidden relative">
                 <div className="absolute top-0 right-0 p-4 opacity-10">
                    <BarChart size={80} />
                 </div>
                 <h4 className="text-lg font-black uppercase tracking-tight mb-2">Analytics Engine</h4>
                 <p className="text-brand-200 text-xs font-medium leading-relaxed mb-6">Reach detailed insights about how many developers are viewing your profile this month.</p>
                 <button className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition">
                    Unlock Premium Insights
                 </button>
              </div>
           </div>

           {/* Listings Table */}
           <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden min-h-[400px]">
                 <div className="p-8 border-b border-gray-50 flex justify-between items-center">
                    <h2 className="text-xl font-black text-gray-900">Your Active Records</h2>
                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{listings.length} Results</span>
                 </div>
                 
                 {listings.length > 0 ? (
                   <div className="divide-y divide-gray-50">
                      {listings.map(pro => (
                        <div key={pro.id} className="p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50 transition">
                           <div>
                              <div className="flex items-center gap-3 mb-1">
                                 <h4 className="font-black text-gray-900">{pro.businessName}</h4>
                                 <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${
                                   pro.tier === 'EXCLUSIVE' ? 'bg-amber-100 text-amber-800' : 'bg-brand-100 text-brand-800'
                                 }`}>{pro.tier}</span>
                              </div>
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">{pro.category} • {pro.location.suburb}, {pro.location.state}</p>
                           </div>
                           <div className="flex items-center gap-2">
                              <Link 
                                to={`/professional/${pro.id}`} 
                                className="flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-xl text-[10px] font-black text-gray-600 hover:text-brand-600 hover:border-brand-200 transition shadow-sm uppercase tracking-widest"
                              >
                                 <ExternalLink size={14} /> View Profile
                              </Link>
                              <Link 
                                to={`/listing-setup?edit=${pro.id}`} 
                                className="flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-xl text-[10px] font-black text-gray-600 hover:text-brand-600 hover:border-brand-200 transition shadow-sm uppercase tracking-widest"
                              >
                                 <Edit3 size={14} /> Edit Details
                              </Link>
                           </div>
                        </div>
                      ))}
                   </div>
                 ) : (
                   <div className="p-24 text-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <PlusCircle className="text-gray-200" size={32} />
                      </div>
                      <p className="text-gray-400 font-medium italic mb-6">You haven't created any professional listings yet.</p>
                      <Link to="/join" className="bg-brand-900 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition shadow-lg">
                        Create Your First Listing
                      </Link>
                   </div>
                 )}
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
